import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Box,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  CheckCircle,
  Schedule,
  Error,
  HourglassEmpty,
  Person,
  CalendarToday,
  GetApp,
  Visibility,
  Description,
  Business,
} from "@material-ui/icons";
import { BoxRenderResult } from "./RenderHTML";
import JSONViewer from "./JSONViewer";
import PopUp from "./PopUp";
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from "../context/ApiKeyContext";
import { Memory } from "@material-ui/icons";
import FileDownload from "./FileDownload";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    overflow: "auto",
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: "var(--ikaro-gray-light)",
  },
  tableHeaderCell: {
    fontWeight: "bold",
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "var(--ikaro-gray-light)",
    },
  },
  statusChip: {
    fontSize: "0.75rem",
    height: "24px",
    minWidth: "80px",
  },
  statusIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(0.5),
  },
  actionButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  cellContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  fileInfo: {
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#666666",
  },
  // Inferências popup styles
  inferencesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  inferencesCost: {
    fontWeight: 'bold',
    color: 'var(--ikaro-gray-darker)'
  },
  inferencesContainer: {
    display: 'flex',
    height: '60vh',
    minHeight: 300,
  },
  inferencesList: {
    overflowY: 'auto',
    borderRight: '1px solid var(--ikaro-gray-light)',
  },
  inferencesListItem: {
    padding: theme.spacing(1),
    cursor: 'pointer',
    borderBottom: '1px solid var(--ikaro-gray-light)',
    '&:hover': {
      backgroundColor: 'var(--ikaro-gray-light)'
    }
  },
  inferencesListItemActive: {
    backgroundColor: 'rgba(100, 49, 132, 0.08)'
  },
  splitter: {
    width: 6,
    cursor: 'col-resize',
    backgroundColor: 'var(--ikaro-gray-light)'
  },
  inferencesDetail: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2)
  },
  panel: {
    border: '1px solid var(--ikaro-gray-light)',
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    textAlign: 'left'
  },
  panelTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: 'var(--ikaro-gray-dark)'
  },
  monospaced: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  // Error popup styles
  errorContent: {
    maxHeight: '60vh',
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: 'rgba(244, 67, 54, 0.05)',
    border: '1px solid rgba(244, 67, 54, 0.2)',
    borderRadius: 4,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '0.875rem'
  },
  errorTitle: {
    color: 'var(--ikaro-status-warning-text)',
    marginBottom: theme.spacing(2),
    fontWeight: 'bold'
  },
  eyeIcon: {
    fontSize: '1rem',
    color: 'var(--ikaro-status-warning-text)',
    cursor: 'pointer'
  }
}));

const JobTable = ({
  jobs,
  columns,
  customActions,
  sortConfig,
  onSort,
  onJobAction,
  emptyStateMessage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('jobs');
  const { t: tCommon } = useTranslation('common');
  const { token, user } = useAuth();
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [contentPopUp, setContentPopUp] = useState("");
  // Error popup state
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorContent, setErrorContent] = useState("");
  // Inferências state
  const [inferencesOpen, setInferencesOpen] = useState(false);
  const [inferencesLoading, setInferencesLoading] = useState(false);
  const [inferencesError, setInferencesError] = useState(null);
  const [inferencesData, setInferencesData] = useState({ total_cost: 0, inferences: [] });
  const [selectedInferenceHash, setSelectedInferenceHash] = useState(null);
  const [inferenceDetailsLoading, setInferenceDetailsLoading] = useState(false);
  const [inferenceDetails, setInferenceDetails] = useState(null);
  const [leftPaneWidth, setLeftPaneWidth] = useState(150);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleViewDetails = (output_payload) => {
    if (!output_payload) {
      setContentPopUp(tCommon('noResultsAvailable'));
      setPopUpOpen(true);
      return;
    }

    setContentPopUp(<BoxRenderResult result={output_payload} />);
    setPopUpOpen(true);
  };

  const handleViewJson = (jsonData) => {
    if (!jsonData) {
      setContentPopUp(tCommon('noJsonAvailable'));
      setPopUpOpen(true);
      return;
    }
    setContentPopUp(<JSONViewer data={jsonData} />);
    setPopUpOpen(true);
  };

  const handleViewError = (errorDescription) => {
    if (!errorDescription) {
      setErrorContent(t('table.noErrorAvailable'));
    } else {
      setErrorContent(errorDescription);
    }
    setErrorPopupOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "DONE":
        return <CheckCircle className={classes.statusIcon} />;
      case "RUNNING":
        return <Schedule className={classes.statusIcon} />;
      case "ERROR":
      case "FINAL_ERROR":
        return <Error className={classes.statusIcon} />;
      case "WAITING":
        return <HourglassEmpty className={classes.statusIcon} />;
      default:
        return <HourglassEmpty className={classes.statusIcon} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DONE":
        return { backgroundColor: "var(--ikaro-status-success-bg)", color: "var(--ikaro-status-success-text)" };
      case "RUNNING":
        return { backgroundColor: "var(--ikaro-status-running-bg)", color: "var(--ikaro-status-running-text)" };
      case "ERROR":
      case "FINAL_ERROR":
        return { backgroundColor: "var(--ikaro-status-warning-bg)", color: "var(--ikaro-status-warning-text)" };
      case "WAITING":
        return { backgroundColor: "var(--ikaro-status-warning-bg)", color: "var(--ikaro-status-warning-text)" };
      default:
        return { backgroundColor: "var(--ikaro-gray-light)", color: "var(--ikaro-gray-darker)" };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("pt-BR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const openInferences = async (job) => {
    if (!API_BASE_URL || !token || !job?.id) return;
    try {
      setInferencesError(null);
      setInferencesLoading(true);
      setInferencesOpen(true);
      setInferenceDetails(null);
      setSelectedInferenceHash(null);

      const url = `${API_BASE_URL}/job/get_job_inferences?job_id=${encodeURIComponent(job.id)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferencesData({ total_cost: data.total_cost || 0, inferences: Array.isArray(data.inferences) ? data.inferences : [] });
    } catch (err) {
      setInferencesError(err.message || 'Erro ao carregar inferências');
    } finally {
      setInferencesLoading(false);
    }
  };

  const fetchInferenceDetails = async (hash) => {
    if (!API_BASE_URL || !token || !hash) return;
    try {
      setInferenceDetailsLoading(true);
      setInferenceDetails(null);
      const url = `${API_BASE_URL}/job/get_inference_details?hash=${encodeURIComponent(hash)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferenceDetails(data);
    } catch (err) {
      setInferenceDetails({});
    } finally {
      setInferenceDetailsLoading(false);
    }
  };

  const firstTagOfInference = (inf) => {
    if (!inf) return '-';
    if (inf.input && inf.input.tag) return inf.input.tag;
    if (inf.output && inf.output.tag) return inf.output.tag;
    if (inf.embedding && inf.embedding.tag) return inf.embedding.tag;
    return '-';
  };

  const handleMouseDownOnSplitter = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPaneWidth;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const newWidth = Math.max(120, Math.min(startWidth + dx, 600));
      setLeftPaneWidth(newWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const renderInferenceRightPanel = () => {
    if (inferenceDetailsLoading) {
      return <Typography variant="body2">{tCommon('loading')}...</Typography>;
    }
    if (!inferenceDetails || Object.keys(inferenceDetails).length === 0) {
      return (
        <Typography variant="body2" className={classes.monospaced}>
          {t('inferences.noResultForHash', { hash: selectedInferenceHash || '-' })}
        </Typography>
      );
    }

    if (inferenceDetails.type === 'chat') {
      const inputTokens = inferenceDetails.costs?.input ?? 0;
      const outputTokens = inferenceDetails.costs?.output ?? 0;
      const systemMessage = inferenceDetails.system_message;
      const userMessage = inferenceDetails.user_message;
      const result = inferenceDetails.result;
      const pdfFile = inferenceDetails.pdf; // { filename, token, html_preview_token? }
      const imageBase64 = inferenceDetails.image_base64;
      const imageMediaType = inferenceDetails.image_media_type;

      return (
        <Box>
          <Typography variant="h6" gutterBottom>{t('inferences.chatTitle')}</Typography>
          <Box className={classes.panel}>
            <Typography className={classes.panelTitle}>{t('inferences.consumption')}</Typography>
            <Typography variant="body2">{t('inferences.inputTokens')}: {inputTokens}</Typography>
            <Typography variant="body2">{t('inferences.outputTokens')}: {outputTokens}</Typography>
          </Box>

          {systemMessage && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.systemMessage')}</Typography>
              <Typography variant="body2" className={classes.monospaced}>{systemMessage}</Typography>
            </Box>
          )}

          {userMessage && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.userMessage')}</Typography>
              <Typography variant="body2" className={classes.monospaced}>{userMessage}</Typography>
            </Box>
          )}

          {imageBase64 && imageMediaType && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.image')}</Typography>
              <img alt="inference" src={`data:${imageMediaType};base64,${imageBase64}`} style={{ maxWidth: '100%' }} />
            </Box>
          )}

          {pdfFile && pdfFile.filename && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.file')}</Typography>
              <FileDownload file={pdfFile} showError={false} />
            </Box>
          )}

          {result && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.result')}</Typography>
              <Typography variant="body2" className={classes.monospaced}>{result}</Typography>
            </Box>
          )}
        </Box>
      );
    }

    if (inferenceDetails.type === 'embedding') {
      const tokens = inferenceDetails.costs?.tokens ?? 0;
      const dimension = inferenceDetails.dimension ?? '-';
      const text = inferenceDetails.text;
      const vector = Array.isArray(inferenceDetails.result) ? inferenceDetails.result : [];

      const prettyVector = (() => {
        const parts = [];
        const perLine = 8;
        for (let i = 0; i < vector.length; i += perLine) {
          const slice = vector.slice(i, i + perLine).map((n) => typeof n === 'number' ? n.toFixed(9) : String(n));
          parts.push(`  ${slice.join(', ')}`);
        }
        return `[
${parts.join('\n')}
]`;
      })();

      return (
        <Box>
          <Typography variant="h6" gutterBottom>{t('inferences.embeddingsTitle')}</Typography>
          <Box className={classes.panel}>
            <Typography className={classes.panelTitle}>{t('inferences.consumption')}</Typography>
            <Typography variant="body2">{t('inferences.tokens')}: {tokens}</Typography>
          </Box>
          <Box className={classes.panel}>
            <Typography className={classes.panelTitle}>{t('inferences.dimensionTitle')}</Typography>
            <Typography variant="body2">{t('inferences.dimension')}: {dimension}</Typography>
          </Box>
          {text && (
            <Box className={classes.panel}>
              <Typography className={classes.panelTitle}>{t('inferences.text')}</Typography>
              <Typography variant="body2" className={classes.monospaced}>{text}</Typography>
            </Box>
          )}
          <Box className={classes.panel}>
            <Typography className={classes.panelTitle}>{t('inferences.result')}</Typography>
            <Typography variant="body2" className={classes.monospaced}>{prettyVector}</Typography>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const renderCellContent = (column, job) => {
    const value = job[column.key];

    switch (column.type) {
      case "status":
        return (
          <Box className={classes.cellContent}>
            <Chip
              icon={getStatusIcon(value)}
              label={column.formatValue ? column.formatValue(value) : value}
              className={classes.statusChip}
              style={getStatusColor(value)}
            />
            {/* Ícone de olho para visualizar erro se o usuário tem permissão e o job está em análise */}
            {user && user.permissions && user.permissions.includes('JOB_ERROR_VIEWER') && 
             (value?.toUpperCase() === 'ERROR' || value?.toUpperCase() === 'FINAL_ERROR') && job.error_description && (
              <Tooltip title={t('table.viewError')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewError(job.error_description);
                  }}
                  className={classes.actionButton}
                >
                  <Visibility className={classes.eyeIcon} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );

      case "date":
        return (
                      <Tooltip
              title={
                <Box>
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {t('table.jobDetailsTitle')}
                  </Typography>
                  <Typography variant="body2">{t('table.jobId')}: {job.id || "-"}</Typography>
                  <Typography variant="body2">
                    {t('table.externalId')}: {job.external_id || "-"}
                  </Typography>
                </Box>
              }
              arrow
            >
            <Box className={classes.cellContent} style={{ cursor: "help" }}>
              <CalendarToday style={{ fontSize: "1rem", color: "var(--ikaro-gray-dark)" }} />
              <Typography variant="body2">{formatDate(value)}</Typography>
              {user && user.permissions && user.permissions.includes('INFERENCES_VIEWER') && (
                <Tooltip title={t('inferences.viewInferences')}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openInferences(job); }}>
                    <Memory style={{ fontSize: '1rem', color: 'var(--ikaro-purple-accent)' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Tooltip>
        );

      case "user":
        return (
          <Box className={classes.cellContent}>
            <Person style={{ fontSize: "1rem", color: "var(--ikaro-gray-dark)" }} />
            <Typography variant="body2">{value || "-"}</Typography>
          </Box>
        );

      case "files":
        return (
          <Box>
            {job.arquivo_pdf && (
              <Box className={classes.fileInfo}>
                <Description style={{ fontSize: "0.875rem" }} />
                <Typography variant="caption">{job.arquivo_pdf}</Typography>
              </Box>
            )}
            {job.template && (
              <Box className={classes.fileInfo}>
                <Description style={{ fontSize: "0.875rem" }} />
                <Typography variant="caption">{job.template}</Typography>
              </Box>
            )}
            {job.input_payload && (
              <Button
                onClick={() => handleViewJson(job.input_payload)}
                startIcon={<Visibility style={{ fontSize: "1rem" }} />}
              >
                {t('table.viewInput')}
              </Button>
            )}
          </Box>
        );

      case "outputs":
        return job.resultados || job.output_payload ? (
          <Box className={classes.cellContent}>
            {job.resultados && (
              <>
                <GetApp style={{ fontSize: "1rem", color: "var(--ikaro-status-success)" }} />
                <Typography variant="body2" style={{ color: "var(--ikaro-status-success)" }}>
                  {job.resultados || t('common:available')}
                </Typography>
              </>
            )}
            {job.output_payload && (
              <Button
                onClick={() => handleViewDetails(job.output_payload)}
                startIcon={<Visibility style={{ fontSize: "1rem" }} />}
              >
                {t('table.viewResult')}
              </Button>
            )}
          </Box>
        ) : (
          <Typography variant="body2" style={{ color: "var(--ikaro-gray-dark)" }}>
            {tCommon('notAvailable')}
          </Typography>
        );

      case "external_id":
        if (!value) return "-";
        const shortId =
          value.length > 5 ? `${value.substring(0, 5)}...` : value;
        return (
          <Tooltip title={`${t('table.externalId')}: ${value}`} arrow>
            <Typography
              variant="body2"
              style={{ cursor: "help", color: "var(--ikaro-status-running)" }}
            >
              {shortId}
            </Typography>
          </Tooltip>
        );

      case "cell":
        return (
          <Chip
            icon={<Business style={{ fontSize: "0.875rem" }} />}
            label={value}
            size="small"
            style={{ backgroundColor: "var(--ikaro-gray-light)", color: "var(--ikaro-gray-darker)" }}
          />
        );

      case "actions":
        return (
          <Box>
            {/* Ação padrão de visualizar */}
            <Tooltip title={t('table.viewDetails')}>
              <IconButton
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onJobAction && onJobAction("view", job);
                }}
              >
                <Visibility style={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>

            {/* Download se houver resultados */}
            {(job.resultados || job.output_payload) && (
              <Tooltip title={t('table.downloadResults')}>
                <IconButton
                  className={classes.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJobAction && onJobAction("download", job);
                  }}
                >
                  <GetApp style={{ fontSize: "1rem", color: "var(--ikaro-status-success)" }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Ações customizadas */}
            {customActions.map((action, index) => (
              <Tooltip key={index} title={action.tooltip}>
                <IconButton
                  className={classes.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJobAction && onJobAction(action.key, job);
                  }}
                  disabled={action.disabled && action.disabled(job)}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        );

      case "custom":
        // Suporte para células customizadas
        if (column.renderCell) {
          return column.renderCell(job);
        }
        return (
          <Typography variant="body2">
            {column.formatValue ? column.formatValue(value) : value || "-"}
          </Typography>
        );

      default:
        return (
          <Typography variant="body2">
            {column.formatValue ? column.formatValue(value) : value || "-"}
          </Typography>
        );
    }
  };

  const handleSort = (column) => {
    if (column.sortable !== false) {
      onSort && onSort(column.key);
    }
  };

  if (jobs.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          {emptyStateMessage || t('table.emptyState')}
        </Typography>
        <Typography variant="body2">
          {t('table.emptyStateDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer className={classes.tableContainer}>
      {/* Popup existente */}
      <PopUp
        open={popUpOpen}
        onClose={() => setPopUpOpen(false)}
        title={""}
        children={contentPopUp}
      />

      {/* Popup de Erro */}
      <PopUp
        open={errorPopupOpen}
        onClose={() => setErrorPopupOpen(false)}
        title=""
      >
        <Box>
          <Typography variant="h6" className={classes.errorTitle}>
            {t('table.errorDetailsTitle')}
          </Typography>
          <Box className={classes.errorContent}>
            {errorContent}
          </Box>
        </Box>
      </PopUp>

      {/* Popup de Inferências */}
      <PopUp
        open={inferencesOpen}
        onClose={() => setInferencesOpen(false)}
        title={""}
      >
        <Box className={classes.inferencesHeader}>
          <Typography variant="h6">{t('inferences.title')}</Typography>
          <Typography variant="body2" className={classes.inferencesCost}>
            {t('inferences.totalCost')}: USD {Number(inferencesData.total_cost || 0).toFixed(6)}
          </Typography>
        </Box>
        <Box className={classes.inferencesContainer}>
          <Box className={classes.inferencesList} style={{ width: leftPaneWidth }}>
            {inferencesLoading ? (
              <Typography variant="body2">{tCommon('loading')}...</Typography>
            ) : inferencesError ? (
              <Typography variant="body2" color="error">{inferencesError}</Typography>
            ) : (inferencesData.inferences || []).map((inf, idx) => {
              const hash = inf.hash;
              const label = `${idx + 1} - ${firstTagOfInference(inf)}`;
              const active = selectedInferenceHash === hash;
              return (
                <Box
                  key={hash || idx}
                  className={`${classes.inferencesListItem} ${active ? classes.inferencesListItemActive : ''}`}
                  onClick={() => { setSelectedInferenceHash(hash); fetchInferenceDetails(hash); }}
                >
                  <Typography variant="body2" style={{ fontWeight: active ? 'bold' : 'normal' }}>{label}</Typography>
                </Box>
              );
            })}
          </Box>
          <Box className={classes.splitter} onMouseDown={handleMouseDownOnSplitter} />
          <Box className={classes.inferencesDetail}>
            {renderInferenceRightPanel()}
          </Box>
        </Box>
      </PopUp>

      <Table className={classes.table}>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                className={classes.tableHeaderCell}
                sortDirection={
                  sortConfig.sortBy === column.key
                    ? sortConfig.sortOrder
                    : false
                }
              >
                {column.sortable !== false ? (
                  <TableSortLabel
                    active={sortConfig.sortBy === column.key}
                    direction={
                      sortConfig.sortBy === column.key
                        ? sortConfig.sortOrder
                        : "asc"
                    }
                    onClick={() => handleSort(column)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job, index) => (
            <TableRow key={job.id || index} className={classes.tableRow}>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {renderCellContent(column, job)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default JobTable;
